import React, { useState, useEffect } from 'react';
import { 
  Save, Image as ImageIcon, Calendar, MapPin, Type, 
  AlertCircle, Check, Music, Youtube, Plus, Trash2, Clock 
} from 'react-feather';
import { API_URL, fetchWithAuth, getImageUrl } from '../../api';

// LISTA PREDEFINIDA
const MUSIC_GENRES = [
  "Reggaeton", "Techno", "House", "Salsa", "Cumbia", 
  "Rock", "Pop", "Hip Hop", "Indie", "Electrónica", 
  "Pachanga", "Trap", "R&B", "Jazz", "80s & 90s"
];

const EventEditTab = ({ eventId, onEventUpdated }) => {
  // --- ESTADOS ---
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Datos Básicos
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    time: '',
    description: '',
    location: '',
    direction: '',
    age_limit: '',
    private: false,
    video: ''
  });

  // Datos Complejos
  const [tags, setTags] = useState([]); // Array de strings seleccionados
  const [lineup, setLineup] = useState([]); 

  // Imagen
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);

  // --- CARGAR DATOS ---
  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const data = await fetchWithAuth(`${API_URL}/events/${eventId}`);
        
        const dateStr = data.date ? new Date(data.date).toISOString().split('T')[0] : '';

        setFormData({
          name: data.name || '',
          date: dateStr,
          time: data.time || '',
          description: data.description || '',
          location: data.location || '',
          direction: data.direction || '',
          age_limit: data.age_limit || '',
          private: data.private || false,
          video: data.video || ''
        });

        setTags(data.tags || []);
        setLineup(data.lineup || []);
        setCurrentImageUrl(data.image_url);

      } catch (err) {
        setError('No se pudo cargar la información del evento.');
      } finally {
        setLoadingData(false);
      }
    };

    if (eventId) fetchEventData();
  }, [eventId]);

  // --- HANDLERS BÁSICOS ---
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  // --- NUEVO HANDLER: SELECCIÓN DE GÉNEROS (TOGGLE) ---
  const toggleGenre = (genre) => {
    if (tags.includes(genre)) {
      setTags(tags.filter(t => t !== genre)); // Si ya está, lo quita
    } else {
      setTags([...tags, genre]); // Si no está, lo agrega
    }
  };

  // --- HANDLERS: LINEUP ---
  const handleAddLineupItem = () => {
    setLineup([...lineup, { name: '', time: '' }]);
  };

  const handleRemoveLineupItem = (index) => {
    const newLineup = lineup.filter((_, i) => i !== index);
    setLineup(newLineup);
  };

  const handleLineupChange = (index, field, value) => {
    const newLineup = [...lineup];
    newLineup[index][field] = value;
    setLineup(newLineup);
  };

  // --- SUBMIT ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMsg('');

    try {
      // 1. Subir Imagen
      let finalImageUrl = currentImageUrl;
      if (imageFile) {
        const formDataImg = new FormData();
        formDataImg.append('file', imageFile);
        const imageResponse = await fetchWithAuth(`${API_URL}/events/${eventId}/upload-image`, {
          method: 'POST',
          body: formDataImg
        });
        finalImageUrl = imageResponse.image_url;
      }

      // 2. Actualizar Datos
      const updatePayload = {
        ...formData,
        image_url: finalImageUrl,
        age_limit: formData.age_limit ? parseInt(formData.age_limit) : null,
        tags: tags,     
        lineup: lineup  
      };

      const updatedEvent = await fetchWithAuth(`${API_URL}/events/${eventId}`, {
        method: 'PUT',
        body: JSON.stringify(updatePayload)
      });

      setSuccessMsg('¡Evento actualizado correctamente!');
      
      if (onEventUpdated) {
        onEventUpdated(updatedEvent);
      }

      setTimeout(() => setSuccessMsg(''), 3000);

    } catch (err) {
      setError(typeof err.message === 'string' ? err.message : 'Error al guardar los cambios.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingData) return <div className="py-20 text-center text-white/50 animate-pulse">Cargando datos...</div>;

  return (
    <div className="w-full max-w-4xl mx-auto pb-20 pt-8 fade-in px-4">
      
      <div className="bg-black/40 backdrop-blur-md rounded-2xl border border-white/10 overflow-hidden">
        <div className="p-6 border-b border-white/10 bg-white/5">
           <h2 className="text-xl font-light tracking-wider text-white text-center">
             EDITAR EVENTO
           </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-10">
          
          {/* --- 1. IMAGEN --- */}
          <div className="flex flex-col items-center justify-center">
             <div className="relative group w-full max-w-xs aspect-[3/4] rounded-xl overflow-hidden border-2 border-dashed border-white/20 bg-black/20 hover:border-white/40 transition-colors shadow-2xl">
                {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : currentImageUrl ? (
                    <img src={getImageUrl(currentImageUrl)} alt="Current" className="w-full h-full object-cover opacity-60 group-hover:opacity-40 transition-opacity" />
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-white/30 p-6 text-center">
                        <ImageIcon size={48} className="mb-2" />
                        <span>Subir portada</span>
                    </div>
                )}
                <input type="file" accept="image/*" onChange={handleImageChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
             </div>
          </div>

          {/* --- 2. INFORMACIÓN BÁSICA --- */}
          <div className="space-y-6">
              <h3 className="text-sm font-bold text-white/50 uppercase tracking-widest border-b border-white/10 pb-2">Información General</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-400 mb-2">Nombre</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required
                        className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Fecha</label>
                    <input type="date" name="date" value={formData.date} onChange={handleChange} required
                        className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white [color-scheme:dark]" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Hora Inicio</label>
                    <input type="time" name="time" value={formData.time} onChange={handleChange}
                        className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white [color-scheme:dark]" />
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-400 mb-2">Descripción</label>
                    <textarea name="description" rows="3" value={formData.description} onChange={handleChange}
                        className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none" />
                </div>
              </div>
          </div>

          {/* --- 3. UBICACIÓN --- */}
          <div className="space-y-6">
              <h3 className="text-sm font-bold text-white/50 uppercase tracking-widest border-b border-white/10 pb-2">Ubicación</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Lugar (Nombre)</label>
                    <input type="text" name="location" value={formData.location} onChange={handleChange} required
                        className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Dirección</label>
                    <input type="text" name="direction" value={formData.direction} onChange={handleChange}
                        className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50" />
                </div>
              </div>
          </div>

          {/* --- 4. GÉNEROS Y MEDIA (MODIFICADO) --- */}
          <div className="space-y-6">
               <h3 className="text-sm font-bold text-white/50 uppercase tracking-widest border-b border-white/10 pb-2">Multimedia & Estilo</h3>
               
               {/* Video */}
               <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
                        <Youtube size={16} /> Video de YouTube (URL)
                    </label>
                    <input
                        type="text"
                        name="video"
                        value={formData.video}
                        onChange={handleChange}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    />
               </div>

               {/* Tags PREDEFINIDOS */}
               <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                        <Music size={16} /> Géneros Musicales (Selecciona varios)
                    </label>
                    
                    <div className="flex flex-wrap gap-2">
                        {MUSIC_GENRES.map((genre) => {
                            const isSelected = tags.includes(genre);
                            return (
                                <button
                                    key={genre}
                                    type="button"
                                    onClick={() => toggleGenre(genre)}
                                    className={`
                                        px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border
                                        ${isSelected 
                                            ? 'bg-white text-black border-white shadow-[0_0_10px_rgba(255,255,255,0.3)] scale-105' 
                                            : 'bg-black/30 text-white/40 border-white/10 hover:border-white/30 hover:text-white'
                                        }
                                    `}
                                >
                                    {genre}
                                </button>
                            );
                        })}
                    </div>
               </div>
          </div>

          {/* --- 5. LINEUP --- */}
          <div className="space-y-6">
            <div className="flex justify-between items-end border-b border-white/10 pb-2">
                <h3 className="text-sm font-bold text-white/50 uppercase tracking-widest">Lineup & Horarios</h3>
            </div>
            
            <div className="space-y-3">
                {lineup.map((item, index) => (
                    <div key={index} className="flex flex-col sm:flex-row gap-3 items-start sm:items-center bg-white/5 p-3 rounded-xl border border-white/5 animate-in fade-in slide-in-from-bottom-2">
                        <div className="relative w-full sm:w-32">
                             <div className="absolute left-3 top-3 text-white/30 pointer-events-none"><Clock size={16} /></div>
                             <input 
                                type="text"
                                placeholder="Hora" 
                                value={item.time}
                                onChange={(e) => handleLineupChange(index, 'time', e.target.value)}
                                className="w-full pl-9 pr-3 py-2 bg-black/50 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-white/30"
                             />
                        </div>
                        <div className="flex-1 w-full">
                             <input 
                                type="text"
                                placeholder="Nombre del Artista / DJ" 
                                value={item.name}
                                onChange={(e) => handleLineupChange(index, 'name', e.target.value)}
                                className="w-full px-3 py-2 bg-black/50 border border-white/10 rounded-lg text-white font-medium focus:outline-none focus:border-white/30"
                             />
                        </div>
                        <button 
                            type="button" 
                            onClick={() => handleRemoveLineupItem(index)}
                            className="p-2 text-white/30 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors self-end sm:self-center"
                        >
                            <Trash2 size={18} />
                        </button>
                    </div>
                ))}

                <button 
                    type="button" 
                    onClick={handleAddLineupItem}
                    className="w-full py-3 border border-dashed border-white/20 rounded-xl text-white/50 hover:text-white hover:border-white/40 hover:bg-white/5 transition-all flex items-center justify-center gap-2"
                >
                    <Plus size={18} /> Agregar Artista
                </button>
            </div>
          </div>

          {/* Toggle de Evento Privado */}
          <div className="md:col-span-2 flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-xl mt-2">
              <div>
                  <h4 className="text-white font-medium">Evento Privado</h4>
                  <p className="text-sm text-gray-400 mt-1">
                      Oculta el evento del feed principal. Solo se podrá acceder con el link directo.
                  </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input 
                      type="checkbox" 
                      name="private" 
                      checked={formData.private} 
                      onChange={handleChange} 
                      className="sr-only peer" 
                  />
                  <div className="w-14 h-7 bg-black/50 border border-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-400 after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-white peer-checked:after:bg-black transition-colors duration-300"></div>
              </label>
          </div>

          {/* FEEDBACK Y BOTÓN FINAL */}
          <div className="pt-8 border-t border-white/10">
            {error && (
                <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-300">
                    <AlertCircle size={20} /> {error}
                </div>
            )}

            {successMsg && (
                <div className="mb-4 p-4 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center gap-3 text-green-300 animate-fade-in">
                    <Check size={20} /> {successMsg}
                </div>
            )}

            <button
                type="submit"
                disabled={saving}
                className="w-full py-4 bg-white text-black rounded-xl font-bold text-lg hover:bg-gray-200 transition-colors shadow-lg shadow-white/5 disabled:opacity-50 flex items-center justify-center gap-2"
            >
                {saving ? 'Guardando...' : <><Save size={20} /> GUARDAR TODO</>}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default EventEditTab;